import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from "@material-ui/lab";
import clsx from 'clsx';
import React, { useEffect, useState, Fragment } from 'react';
import { Booking, Student, Lesson, Slot } from 'services';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";
import { ErrorMessage } from 'components';
import moment from 'moment';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

let mounted = false;

const CreateBookingModal = props => {
  let { onCreate, reload, open, onClose, className, slot_id, student_id, ...rest } = props;

  const schema = {
    student: {
      presence: slot_id ? { allowEmpty: false, message: "is required" } : null,
    },
    slot: {
      presence: student_id ? { allowEmpty: false, message: "is required" } : null,
    }
  };

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [slots, setSlots] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && error.message));

  const [formState, setFormState] = useState({
    isValid: false,
    values: {
      student: null,
      slot: null,
      lesson: null
    },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    const errors = validate(formState.values, schema);

    mounted && setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]); // eslint-disable-line

  useEffect(() => {
    mounted = true;
    return () => { mounted = false };
  }, []);

  useEffect(() => {
    setFormState({
      isValid: false,
      values: {
        slot: null,
        student: null,
        lesson: null
      },
      touched: {},
      errors: {}
    });
  }, [open]);


  const handleSubmit = event => {
    event.preventDefault();
    setFormState({
      ...formState,
      touched: {
        student: true,
        lesson: true,
        slot: true
      }
    });

    if (Object.keys(formState.errors).length) return;

    mounted && setLoading(true);

    runAsyncBlock(async () => {
      const { student, slot } = formState.values;
      await Booking.create({ lesson_slot_id: slot ? slot.id : slot_id, student_id: student ? student.id : student_id });
      onCreate();
      onClose();
    }).finally(() => {
      mounted && setLoading(false);
    });

  }

  const handleFieldChange = (name, value) => {
    mounted && setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [name]: value
      },
      touched: {
        ...formState.touched,
        [name]: true
      }
    }));
  };

  useEffect(() => {
    setLoading(true);
    runAsyncBlock(async () => {
      if (slot_id) {
        const { models } = await Student.list({ query: { limit: 9999 } });
        if (mounted) {
          setStudents(models);
        }
      } else if (student_id) {
        const { models } = await Lesson.list({ query: { limit: 9999 } });
        if (mounted) {
          setLessons(models);
        }
      }
    }).finally(() => { mounted && setLoading(false) });
  }, []) // eslint-disable-line

  useEffect(() => {
    let lesson = formState.values.lesson;
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        slot: null
      }
    }))
    if (lesson) {
      setLoading(true);
      runAsyncBlock(async () => {
        const { models } = await Slot.list({
          query: {
            limit: 9999, lesson: lesson.id,
            start_at: `${moment().unix()},${moment().add(10, "years").unix()}`,
            sort: "start_at:asc"
          },
        });
        if (mounted) {
          setSlots(models);
        }
      }).finally(() => { mounted && setLoading(false) });
    }
  }, [formState.values.lesson]) // eslint-disable-line

  if (!open) {
    return null;
  }

  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Booking
            </Typography>
            <ErrorMessage message={errorMessage} />
            <Grid className={classes.container} container spacing={3}>
              {slot_id &&
                <Grid item md={12} xs={12}>
                  <Autocomplete
                    id="student"
                    options={students}
                    loading={loading}
                    getOptionLabel={option => `${option.name_en}${option.name_zh ? " " + option.name_zh : ""}`}
                    value={formState.values.student}
                    onChange={(e, value) => handleFieldChange("student", value)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Student"
                        fullWidth
                        variant="outlined"
                        helperText={hasError("student") ? formState.errors.student[0] : null}
                        error={hasError("student")}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <React.Fragment>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </React.Fragment>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
              }
              {student_id && (
                <Fragment>
                  <Grid item md={12} xs={12}>
                    <Autocomplete
                      id="lesson"
                      options={lessons}
                      loading={loading}
                      getOptionLabel={option => `${option.name_en}${option.name_zh ? " " + option.name_zh : ""}`}
                      value={formState.values.lesson}
                      onChange={(e, value) => handleFieldChange("lesson", value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Lesson"
                          fullWidth
                          variant="outlined"
                          helperText={hasError("lesson") ? formState.errors.lesson[0] : null}
                          error={hasError("lesson")}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <Autocomplete
                      id="lesson"
                      options={slots}
                      loading={loading}
                      getOptionLabel={option => `${moment(option.start_at).format("DD/MM/YYYY")} | ${moment(option.start_at).format("HH:mm")} - ${moment(option.end_at).format("HH:mm")}`}
                      value={formState.values.slot}
                      onChange={(e, value) => handleFieldChange("slot", value)}
                      renderInput={params => (
                        <TextField
                          {...params}
                          label="Slot"
                          fullWidth
                          variant="outlined"
                          helperText={hasError("slot") ? formState.errors.slot[0] : null}
                          error={hasError("slot")}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Fragment>
              )}
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}

export default CreateBookingModal;